import { renderProductPopup, cardActionSelector, swapCardAction } from './product-popup.js';
import {
  salePriceLabelHTML,
  comparePriceHTML,
  swatchGroupAttrs,
  swatchButtonHTML,
  staticSwatchHTML,
  labelSwatch,
  setActiveSwatch,
} from './card-a11y.js';
/**
 * Section: Merch Homepage
 * ------------------------------------------------------------------------------
 * Makes a graphql based on the handle request to fetch the product data and renders the product
 * the handle is fetched from the collection handle clicked by the user, the handles are rendered from the liquid file block settings
 * the collection handle is stored in the local storage to maintain the state of the collection
 * Refactored to use vanilla JavaScript instead of jQuery
 */

class Merch extends HTMLElement {
  // ============================================================================
  // CONSTANTS - Static properties for selectors, classes, and default values
  // ============================================================================
  static SELECTORS = {
    COLLECTION_HANDLE: '.collection-handle',
    COLLECTION_HANDLE_ACTIVE: '.collection-handle.active',
    PRODUCTS_GRID: 'products-grid',
    OPEN_FILTERS: '.open-filters',
    SWATCH: '.swatch',
    PRODUCT_GRID_ITEM: '.product-grid-item',
    PARENT_PRODUCT: '.parent-product',
  };

  static CSS_CLASSES = {
    ACTIVE: 'active',
    PARENT_PRODUCT: 'parent-product',
  };

  // Storage keys removed - always start fresh with Liquid-rendered content

  static DEFAULT_VALUES = {
    NUM_PRODUCTS: 24,
    PAGE: 1,
  };

  // ============================================================================
  // CONSTRUCTOR - Initialize component properties
  // ============================================================================
  constructor() {
    super();
    this.numProducts = Merch.DEFAULT_VALUES.NUM_PRODUCTS;
    this.accessToken = '8bcb2ea2ba9acd5b735e7abe4cbca7d0';
    this.graphUrl =
      'https://liquiddeath.myshopify.com/api/2025-07/graphql.json';
    this.cursor = null;
    this.hasNextPage = false;
    this.collection = this.querySelector(
      Merch.SELECTORS.COLLECTION_HANDLE_ACTIVE
    )?.getAttribute('data-handle');
    this.grid = this.querySelector(Merch.SELECTORS.PRODUCTS_GRID);
    this.page = Merch.DEFAULT_VALUES.PAGE;
    this.market = this.grid?.getAttribute('data-market');
    this.context = this.getAttribute('data-market').toUpperCase();
    this.countryClubCustomer = this.getAttribute('countryClubUser');
    this.currency = this.grid?.getAttribute('data-currency');
    this.scrolled = false;
  }
  // ============================================================================
  // LIFECYCLE METHODS - Component initialization and event setup
  // ============================================================================
  connectedCallback() {
    // Clear any stored collection preference - always start with default
    localStorage.removeItem('selectedCollection');

    // Check if we have Liquid-rendered content
    this.isLiquidRendered = this.grid?.getAttribute('data-initial-render') === 'true';

    if (this.isLiquidRendered) {
      // Liquid already rendered products - just enhance with swatches
      this.initFromLiquid();
    } else {
      // Fallback to fetch (shouldn't happen normally)
      this.fetchProducts();
    }

    this.setupEventListeners();
    this.changeCollectionPage();
    this.changeVariant();
    this.pagination();
    this.toggleFilters();
  }

  /**
   * Initialize from Liquid-rendered content
   * Enhances existing products with swatches without re-fetching
   */
  initFromLiquid() {
    // Read pagination state from Liquid
    const paginationData = document.getElementById('pagination-data');
    if (paginationData) {
      try {
        const data = JSON.parse(paginationData.textContent);
        this.hasNextPage = data.hasNextPage;
        this.page = data.currentPage;
      } catch (e) {
        console.error('Error parsing pagination data:', e);
      }
    }

    // Collect related swatch IDs from Liquid-rendered products
    const productItems = this.grid?.querySelectorAll('.product-grid-item[data-related-swatches]') || [];
    const relatedSwatchIds = new Set();

    productItems.forEach((item) => {
      const relatedData = item.getAttribute('data-related-swatches');
      if (relatedData) {
        try {
          const ids = JSON.parse(relatedData);
          ids.forEach((id) => relatedSwatchIds.add(id));
        } catch (e) {
          // Ignore parse errors
        }
      }
    });

    // Fetch related products for swatches if any
    if (relatedSwatchIds.size > 0) {
      const idsArray = Array.from(relatedSwatchIds);
      this.getRelatedProduct(idsArray)
        .then((html) => {
          if (html && this.grid) {
            this.grid.insertAdjacentHTML('beforeend', html);
          }
          this.processSwatches();
          this.enhanceLiquidSwatches();
        })
        .catch((error) => {
          console.error('Error fetching related products:', error);
          this.processSwatches();
          this.enhanceLiquidSwatches();
        });
    } else {
      this.processSwatches();
      this.enhanceLiquidSwatches();
    }
  }

  /**
   * Enhance Liquid-rendered swatches with full swatch sets
   */
  enhanceLiquidSwatches() {
    const swatchContainers = this.grid?.querySelectorAll('.product-swatches[data-needs-swatches="true"]') || [];

    swatchContainers.forEach((container) => {
      const productItem = container.closest('.product-grid-item');
      if (!productItem) return;

      const productId = productItem.dataset.productId;
      const relatedData = productItem.getAttribute('data-related-swatches');

      if (!relatedData) return;

      try {
        const relatedIds = JSON.parse(relatedData);
        if (relatedIds.length === 0) return;

        // Build full swatch set (current product + related)
        const allIds = [productId, ...relatedIds.map((id) => this.decodeId(id))];
        const sortedIds = [...new Set(allIds)].sort((a, b) => a - b);

        // Clear existing swatches and rebuild
        container.innerHTML = sortedIds
          .map((id) =>
            swatchButtonHTML({ id, active: String(id) === String(productId) })
          )
          .join('');

        // Mark as processed
        container.removeAttribute('data-needs-swatches');
      } catch (e) {
        console.error('Error enhancing swatches:', e);
      }
    });

    // Re-process swatches to add color classes
    this.processSwatches();
  }

  /**
   * Set up all event listeners for the component
   */
  setupEventListeners() {
    // Handle all click events including swatches and filters
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  /**
   * Handle all document click events
   * @param {MouseEvent} event - The click event
   */
  handleDocumentClick(event) {
    const target = event.target;

    // Handle swatch clicks
    if (target.classList.contains('swatch')) {
      this.handleSwatchClick(target);
    }

    // Update filter counts on any click
    this.updateFilterCounts();
  }

  /**
   * Handle swatch click events - updates product card in-place instead of reordering
   * @param {HTMLElement} swatch - The clicked swatch element
   */
  handleSwatchClick(swatch) {
    const swatchProductId = swatch.dataset.swatch;
    const originalParent = swatch.closest(Merch.SELECTORS.PRODUCT_GRID_ITEM);

    if (swatchProductId && originalParent && !swatch.classList.contains('active')) {
      // Get the original product ID from the card
      const originalProductId = originalParent.dataset.productId;
      const currentlyDisplayed = originalParent.dataset.displayedProductId || originalProductId;

      // If already displaying this product, just update active state
      if (currentlyDisplayed === swatchProductId) {
        this.updateSwatchActiveState(swatch, originalParent);
        return;
      }

      // Find the related product card (hidden parent-product) with matching ID
      const escapedId = CSS.escape(String(swatchProductId));
      const allMatching = document.querySelectorAll(`.product-grid-item.${escapedId}`);
      let relatedProduct = null;

      for (const card of allMatching) {
        if (card !== originalParent) {
          relatedProduct = card;
          break;
        }
      }

      if (relatedProduct) {
        // Store original data before first update
        if (!originalParent.dataset.originalDataStored) {
          const originalImage = originalParent.querySelector('.lqip-container img');
          const originalLink = originalParent.querySelector('a[href*="/products/"]');
          const originalTitle = originalParent.querySelector('.product-grid-item__title');
          originalParent.dataset.originalAction = originalParent.querySelector(cardActionSelector)?.outerHTML || '';
          const originalCta = originalParent.querySelector('.product-cta');

          if (originalImage) {
            originalParent.dataset.originalImage = originalImage.src;
            originalParent.dataset.originalAlt = originalImage.alt || '';
          }
          if (originalLink) {
            originalParent.dataset.originalLink = originalLink.getAttribute('href');
          }
          if (originalTitle) {
            originalParent.dataset.originalTitle = originalTitle.textContent;
          }
          if (originalCta) {
            originalParent.dataset.originalCtaHandle = originalCta.dataset.handle || '';
            originalParent.dataset.originalCtaColor = originalCta.dataset.color || '';
            originalParent.dataset.originalCtaId = originalCta.dataset.id || '';
          }
          originalParent.dataset.originalDataStored = 'true';
        }

        // Update image with LQIP
        const currentImage = originalParent.querySelector('.lqip-container img');
        const relatedImage = relatedProduct.querySelector('.lqip-container img');
        const currentLqipContainer = originalParent.querySelector('.lqip-container');

        if (currentImage && relatedImage && currentLqipContainer) {
          // Set LQIP background for the new image
          const lqipUrl = this.getLqipUrl(relatedImage.src);
          currentLqipContainer.classList.remove('lqip-loaded');
          currentLqipContainer.style.backgroundImage = `url('${lqipUrl}')`;
          currentImage.src = relatedImage.src;
          currentImage.alt = relatedImage.alt || '';
        }

        // Update links
        const currentLinks = originalParent.querySelectorAll('a[href*="/products/"]');
        const relatedLink = relatedProduct.querySelector('a[href*="/products/"]');
        if (relatedLink) {
          const newHref = relatedLink.getAttribute('href');
          currentLinks.forEach((link) => link.setAttribute('href', newHref));
        }

        // Update title
        const currentTitle = originalParent.querySelector('.product-grid-item__title');
        const relatedTitle = relatedProduct.querySelector('.product-grid-item__title');
        if (currentTitle && relatedTitle) {
          currentTitle.textContent = relatedTitle.textContent;
        }

        // Update CTA
        swapCardAction(originalParent, relatedProduct);

        // Track currently displayed product
        originalParent.dataset.displayedProductId = swatchProductId;

        // Update swatch active state
        this.updateSwatchActiveState(swatch, originalParent);
      } else if (swatchProductId === originalProductId && originalParent.dataset.originalDataStored === 'true') {
        // Restore original data
        const currentImage = originalParent.querySelector('.lqip-container img');
        const currentLqipContainer = originalParent.querySelector('.lqip-container');

        if (currentImage && originalParent.dataset.originalImage && currentLqipContainer) {
          const lqipUrl = this.getLqipUrl(originalParent.dataset.originalImage);
          currentLqipContainer.classList.remove('lqip-loaded');
          currentLqipContainer.style.backgroundImage = `url('${lqipUrl}')`;
          currentImage.src = originalParent.dataset.originalImage;
          currentImage.alt = originalParent.dataset.originalAlt || '';
        }

        const currentLinks = originalParent.querySelectorAll('a[href*="/products/"]');
        if (originalParent.dataset.originalLink) {
          currentLinks.forEach((link) => link.setAttribute('href', originalParent.dataset.originalLink));
        }

        const currentTitle = originalParent.querySelector('.product-grid-item__title');
        if (currentTitle && originalParent.dataset.originalTitle) {
          currentTitle.textContent = originalParent.dataset.originalTitle;
        }

        const currentCta = originalParent.querySelector(cardActionSelector);
        if (currentCta && originalParent.dataset.originalAction) currentCta.outerHTML = originalParent.dataset.originalAction;

        originalParent.dataset.displayedProductId = originalProductId;
        this.updateSwatchActiveState(swatch, originalParent);
      } else {
        // Fallback - just update active state
        this.updateSwatchActiveState(swatch, originalParent);
      }
    } else if (!swatchProductId) {
      // If no handle, just update active state on the current product
      this.updateSwatchActiveState(swatch, originalParent);
    }
  }

  /**
   * Update swatch active state - consolidated active state management
   * @param {HTMLElement} swatch - The clicked swatch element
   * @param {HTMLElement} parent - The parent product container (optional, will find if not provided)
   */
  updateSwatchActiveState(swatch, parent = null) {
    // Use provided parent or find it via closest
    const targetParent =
      parent || swatch.closest(Merch.SELECTORS.PRODUCT_GRID_ITEM);

    if (targetParent) {
      // Find the target swatch by data-swatch attribute (fresh reference)
      const targetSwatchData = swatch.dataset.swatch;
      const targetSwatch = targetParent.querySelector(
        `[data-swatch="${targetSwatchData}"]`
      );

      // Move the active class and aria-pressed to the target swatch
      setActiveSwatch(targetSwatch, targetParent);
    }
  }

  // ============================================================================
  // EVENT HANDLING METHODS - User interaction and navigation
  // ============================================================================
  /**
   * Set up filter toggle functionality
   */
  toggleFilters() {
    const openFiltersButton = document.querySelector(
      Merch.SELECTORS.OPEN_FILTERS
    );
    if (openFiltersButton) {
      openFiltersButton.addEventListener('click', () => {
        const activeCollectionHandle = document.querySelector(
          Merch.SELECTORS.COLLECTION_HANDLE_ACTIVE
        );
        const collectionHandle = activeCollectionHandle?.dataset.handle;
        if (collectionHandle) {
          window.location.href = `/collections/${collectionHandle}?openFilter=true`;
        }
      });
    }
  }

  /**
   * Set up collection page change functionality
   */
  changeCollectionPage() {
    const collectionHandles = document.querySelectorAll(
      Merch.SELECTORS.COLLECTION_HANDLE
    );

    // Determine the default collection (first one in the list, rendered by Liquid)
    const firstHandle = collectionHandles[0];
    this.defaultCollection = firstHandle?.dataset.handle;

    collectionHandles.forEach((handle) => {
      handle.addEventListener('click', (event) => {
        event.preventDefault();

        // Update active state
        this.setActiveCollectionHandle(handle);

        // Update collection
        this.cursor = null;
        this.collection = handle.dataset.handle;

        // If selecting the default collection, reload to get Liquid-rendered content
        if (this.collection === this.defaultCollection) {
          window.location.reload();
          return;
        }

        // Fetch products for other collections
        this.fetchProducts();
      });
    });
  }

  /**
   * Set the active collection handle and remove active from siblings
   * @param {HTMLElement} activeHandle - The handle to make active
   */
  setActiveCollectionHandle(activeHandle) {
    const allHandles = document.querySelectorAll(
      Merch.SELECTORS.COLLECTION_HANDLE
    );
    allHandles.forEach((handle) => {
      handle.classList.remove(Merch.CSS_CLASSES.ACTIVE);
    });
    activeHandle.classList.add(Merch.CSS_CLASSES.ACTIVE);
  }


  // ============================================================================
  // PAGINATION AND SCROLL METHODS - Infinite scroll functionality
  // ============================================================================
  /**
   * Set up infinite scroll pagination
   */
  pagination() {
    const section = document.querySelector(Merch.SELECTORS.PRODUCTS_GRID);
    if (!section) return;

    window.addEventListener('scroll', () => {
      const scroll = window.pageYOffset;
      const sectionHeight = section.offsetHeight;
      const sectionOffsetTop = section.offsetTop;
      const targetHeight = sectionHeight + sectionOffsetTop;
      const scrollBottom = scroll + window.innerHeight;

      if (!this.scrolled) {
        if (scrollBottom >= targetHeight) {
          this.page++;
          if (this.hasNextPage) {
            this.hasNextPage = false;
            // For Liquid-rendered content, we need to fetch enough products to get new ones
            if (this.isLiquidRendered) {
              this.isLiquidRendered = false; // Switch to JS mode
              this.cursor = null; // Start from beginning
              // Temporarily increase fetch count to include products beyond what Liquid rendered
              const baseCount = Merch.DEFAULT_VALUES.NUM_PRODUCTS;
              this.numProducts = this.page * baseCount;
              this.fetchProducts(true);
              // Reset numProducts for subsequent fetches (cursor-based pagination)
              this.numProducts = baseCount;
            } else {
              this.fetchProducts(true);
            }
          }
          this.scrolled = true;
        }
      } else {
        if (scrollBottom < targetHeight) {
          this.scrolled = false;
        }
      }
    });
  }

  /**
   * Get IDs of products already rendered in the DOM
   */
  getExistingProductIds() {
    const existingProducts = this.grid?.querySelectorAll('.product-grid-item') || [];
    const ids = new Set();
    existingProducts.forEach(el => {
      el.classList.forEach(cls => {
        if (/^\d+$/.test(cls)) {
          ids.add(cls);
        }
      });
    });
    return ids;
  }

  // ============================================================================
  // GRAPHQL METHODS - Product data fetching and query building
  // ============================================================================
  /**
   * Build GraphQL query for product data
   * @returns {string} The GraphQL query string
   */
  buildProductQuery() {
    const arg = this.cursor
      ? `first: ${this.numProducts} after: "${this.cursor}"`
      : `first: ${this.numProducts}`;

    return `query @inContext(country: ${this.context}){
                collection(handle: "${this.collection}") {
                    products(${arg}) {
                        edges {
                            cursor
            node {
                                handle
                                id
                                title
                                featuredImage {
                                    transformedSrc(maxWidth: 700)
                                }
              buttonDisabled: metafield(namespace: "custom", key: "product_button_disabled") { value }

              popupEnabled: metafield(namespace: "custom", key: "klaviyo_popup_enabled") { value }
              popupFormId: metafield(namespace: "custom", key: "klaviyo_form_id") { value }
              popupButtonText: metafield(namespace: "custom", key: "klaviyo_button_text") { value }
              metafields(identifiers: [
                {namespace: "custom", key: "swatch"}, 
                {namespace: "pdp", key: "amazon_link"}, 
                {namespace: "pdp", key: "amazon_button_text"}, 
                {namespace: "pdp", key: "announcement_tag"}, 
                {namespace: "custom", key: "related_swatches"}
              ]) {
                                  value
                                }
                                tags
                                variants(first: 100) {
                                    edges {
                                        node {
                                            id 
                    price {
                                                amount
                                            }
                                            compareAtPrice {
                                              amount
                                            }
                                            title
                                            availableForSale
                                            quantityAvailable
                                        }
                                    }
                                }
                                options {
                                    name
                                    values
                                }
                            }
                        }
                        pageInfo {
                            hasNextPage
                        }
                    }
                }
            }`;
  }
  /**
   * Set up variant change functionality for swatches and filters
   * Note: Event listeners are now handled in setupEventListeners() to avoid conflicts
   */
  changeVariant() {
    // This method is kept for compatibility but event handling is now centralized
    // in setupEventListeners() to prevent duplicate event listeners
  }

  /**
   * Update filter count displays
   */
  updateFilterCounts() {
    const colorCheckboxes = document.querySelectorAll(
      '.filters-wrap input[type="checkbox"][data-type="Color"]:checked'
    );
    const sizeCheckboxes = document.querySelectorAll(
      '.filters-wrap input[type="checkbox"][data-type="Size"]:checked'
    );
    const allCheckboxes = document.querySelectorAll(
      '.filters-wrap input[type="checkbox"]:checked'
    );

    const selectedColorAmount = `(${colorCheckboxes.length})`;
    const selectedSizeAmount = `(${sizeCheckboxes.length})`;
    const selectedFilterAmount = `(${allCheckboxes.length})`;

    // Update filter labels
    const sortSize = document.getElementById('sort-size');
    const resetFilters = document.querySelector('.reset-filters');
    const sortColor = document.getElementById('sort-color');

    if (sortSize) sortSize.textContent = `Shop by Size ${selectedSizeAmount}`;
    if (resetFilters)
      resetFilters.textContent = `RESET ${selectedFilterAmount}`;
    if (sortColor)
      sortColor.textContent = `Shop by Color ${selectedColorAmount}`;
  }

  // ============================================================================
  // UTILITY METHODS - Helper functions for data processing
  // ============================================================================
  /**
   * Decode Shopify GraphQL ID to extract numeric ID
   * @param {string} id - The Shopify GraphQL ID
   * @returns {number|null} The numeric ID or null if invalid
   */
  decodeId(id) {
    if (!id || typeof id !== 'string') {
      console.error('Invalid ID provided to decodeId:', id);
      return null;
    }

    try {
      const shopifyId = id.split('/').pop();
      return parseInt(shopifyId);
    } catch (error) {
      console.error('Error decoding ID:', error);
      return null;
    }
  }

  /**
   * Format a number as a price string with commas and two decimal places
   * @param {number} amount - The price amount
   * @returns {string} Formatted price (e.g., "595,391.00")
   */
  formatPrice(amount) {
    return Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Get LQIP (Low Quality Image Placeholder) URL from full image URL
   * @param {string} imageUrl - The full image URL
   * @returns {string} The LQIP URL with width=20
   */
  getLqipUrl(imageUrl) {
    if (!imageUrl) return '';
    try {
      // Shopify CDN URLs - replace width in the URL path or add width param
      // transformedSrc URLs look like: ...image.jpg?v=123&width=700 or ...image_700x.jpg
      let url = imageUrl;

      // Handle _WIDTHx format in filename (e.g., image_700x.jpg -> image_20x.jpg)
      url = url.replace(/_\d+x(\.(jpg|jpeg|png|gif|webp))/i, '_20x$1');

      // Handle width query param
      if (url.includes('width=')) {
        url = url.replace(/width=\d+/, 'width=20');
      } else if (url.includes('?')) {
        url = url + '&width=20';
      } else {
        url = url + '?width=20';
      }

      return url;
    } catch (error) {
      console.error('Error generating LQIP URL:', error);
      return imageUrl;
    }
  }
  renderTemplate(product, parent) {
    let self = this;
    let item = product.node;
    if (parent) {
      item = product;
    }
    let image = item.featuredImage.transformedSrc;
    let market = this.market;
    if (market === 'us') {
      market = '/';
    } else {
      market = '/en-' + market + '/';
    }

    let productHandle = `data-product-handle="${item.handle}"`;
    const variantPrice = Number(item.variants.edges[0].node.price.amount);
    let price = this.formatPrice(variantPrice);
    let comparePrice = '';

    // Check if this product is excluded from promo
    const isExcluded =
      window.promoExcludedProducts?.includes(item.handle) || false;

    // Apply promotional pricing if enabled and not excluded
    if (
      window.showPromoOriginalPrice &&
      window.promoDiscountPercentage > 0 &&
      !isExcluded
    ) {
      const discountMultiplier = (100 - window.promoDiscountPercentage) / 100;
      price = this.formatPrice(variantPrice * discountMultiplier);

      // If compare_at_price exists, use it; otherwise show original price as slashed
      if (item.variants.edges[0].node.compareAtPrice) {
        comparePrice = comparePriceHTML(
          `$${this.formatPrice(item.variants.edges[0].node.compareAtPrice.amount)}`
        );
      } else {
        comparePrice = comparePriceHTML(`$${this.formatPrice(variantPrice)}`);
      }
    }
    // No promo - show regular pricing with compare_at_price if exists
    else if (item.variants.edges[0].node.compareAtPrice) {
      comparePrice = comparePriceHTML(
        `$${this.formatPrice(item.variants.edges[0].node.compareAtPrice.amount)}`
      );
    }

    let selectedColor =
      item.options.filter((option) => option.name.toLowerCase() == 'color')[0]
        ?.values[0] || null;

    let available = item.variants.edges.filter(
      (variant) => variant.node.availableForSale
    ).length;

    let countryClubProduct = item.tags.includes('country-club-exclusive');

    let swatches = [];
    // Check if the product has swatches loaded from metafields
    if (item.metafields[4]) {
      swatches.push(self.decodeId(item.id));
      JSON.parse(item.metafields[4].value).forEach(function (product) {
        swatches.push(self.decodeId(product));
      });
    }
    swatches = swatches.sort();
    let html = `
            <div class="product-grid-item ${self.decodeId(item.id)} ${
              parent ? 'parent-product' : ''
            }" data-color="${item.metafields[0]?.value}" data-product-id="${self.decodeId(item.id)}">
                <div class="relative">
                  <a href="${market}products/${item.handle}" class="block">
                    <div class="lqip-container" style="aspect-ratio: 1; background-image: url('${self.getLqipUrl(image)}'); background-size: cover; background-position: center;">
                      <img class="w-full h-full object-cover" src="${image}" alt="${item.title}" width="400" height="400" loading="lazy" onload="this.parentElement.classList.add('lqip-loaded'); this.parentElement.style.backgroundImage = 'none';" />
                    </div>
                  </a>`;
    if (item.metafields[3] != null) {
      html += `<span class="default-badge absolute"><span class='block relative -mb-[2px]'>${item.metafields[3].value}</span></span>`;
    }

    html += `
      <span class="absolute sold-out-badge ${
        (available || item.metafields[1] != null) && '!hidden'
      }"><span class='block relative -mb-[2px]'>Sold out</span></span>
    </div>
    <a href="/products/${item.handle}" class="block">
      <div class="text-dark product-grid-item__title">${item.title}</div>
    </a>
    <div class="text-dark product-grid-item__price">${
      comparePrice ? salePriceLabelHTML() : ''
    }${this.currency}${price}${comparePrice ? ' ' + comparePrice : ''}</div>`;

    if (swatches.length || selectedColor) {
      html += `<div class="product-swatches ml-6 md:ml-0 my-2" ${swatchGroupAttrs()}>`;
      if (swatches.length) {
        swatches.forEach(function (swatch) {
          html += swatchButtonHTML({
            id: swatch,
            active: swatch == self.decodeId(item.id),
          });
        });
      } else if (selectedColor) {
        let color = selectedColor
          .toLowerCase()
          .replaceAll(' ', '-')
          .replaceAll('/', '-');
        html += staticSwatchHTML(color, selectedColor);
      }
      html += `</div>`;
    }

    if (item.buttonDisabled?.value === 'true') {
      html += renderProductPopup(item) + '</div>';
    } else if (countryClubProduct && this.countryClubCustomer == 'false') {
      html += `<a href=”/pages/sell-your-soul” class=”text-dark uppercase cursor-pointer font-bold ml-6 md:ml-0”>Country Club Exclusive</a></div>`;
    } else if (renderProductPopup(item)) {
      html += renderProductPopup(item) + '</div>';
    } else if (item.metafields[1] != null) {
      const amazonUrl = item.metafields[1].value.replace(/[\u201C\u201D\u201E\u201F”\u2033\u2036]/g, '').trim();
      const amazonText = (item.metafields[2]?.value || 'Buy on Amazon').replace(/[\u201C\u201D\u201E\u201F”\u2033\u2036]/g, '').trim();
      html += `<a target='_blank' aria-label='Opens in new window' href='${amazonUrl}' class='text-dark uppercase cursor-pointer font-bold ml-6 md:ml-0' data-handle='${item.handle}'>${amazonText}</a>
                </div>`;
    } else if (!available) {
      html += `
    <klaviyo-bis product-id='${this.decodeId(
      item.id
    )}' variant-id='${this.decodeId(item.variants.edges[0].node.id)}'>
    <a
    class='klaviyo-bis-trigger ml-6 md:ml-0 block text-dark uppercase cursor-pointer font-bold bg-white  hover:text-black'
    href='#'
    >Notify Me When Available</a>
    <div
    class='klaviyo-bis-modal-overlay fixed z-50 inset-0 overflow-y-auto hidden bg-black/50 flex items-center justify-center'
    aria-labelledby='modal-title'
    role='dialog'
    aria-modal='true'>
      <div id='klaviyo-bis-modal' class='relative'>
        <div
          class='bg-white relative border border-gray-300 shadow-lg'
          style='padding: 32px 18px 40px 18px; max-width: 460px;'
          id='container'
        >
          <button
            type='button'
            class='close klaviyo-bis-close'
            data-dismiss='modal'
          >
            ×
          </button>
          <h3 class='modal-title'>${item.title}</h3>
          <p>Register to receive a notification when this item comes back in stock.</p>
          <form action='#' class='form-horizontal clearfix'>
            <div class='form-group' style='margin-bottom: 15px;'>
              <div id='variant_select' class='col-sm-12' style='display: none;'>
                <input type='hidden' id='product' value='${this.decodeId(
                  item.variants.edges[0].node.id
                )}'
                ><select id='variants' class='selectpicker form-control input-lg'>`;
      item.variants.edges.forEach((variantEdge) => {
        let variant = variantEdge.node;
        html += `<option value='${this.decodeId(variant.id)}'>${
          variant.title
        }</option>`;
      });
      html += `
                </select>
              </div>
            </div>
            <div class='form-group' style='margin-bottom: 15px;'>
              <div class='col-sm-12'>
                <input
                  required
                  type='email'
                  id='email'
                  placeholder='Email'
                  class='form-control input-lg'
                  value=''
                >
              </div>
              <div id='newsletter_subscription' style='display: none;'>
                <p>
                  <input type='checkbox' id='subscribe_for_newsletter' checked=''
                  ><label for='subscribe_for_newsletter' style='margin-left:10px;'
                    >Add me to your email list.</label
                  >
                </p>
              </div>
            </div>
            <div class='control-group clearfix submit-container'>
              <button type='submit' class='btn btn-notify btn-success btn-lg col-xs-12'>
                Notify me when available
              </button>
            </div>
            <p class='text-sm'>
              By clicking “Notify Me” you agree to be brainwashed by Liquid Death marketing through rare
              (but hilarious) emails and agreeing to our
              <a class='text-[#8a6d35] underline' href='/policies/terms-of-service'>Terms & Conditions</a> /
              <a class='text-[#8a6d35] underline' href='/policies/privacy-policy'>Privacy Policy</a>
            </p>
            <div id='error_message' class='error_message alert alert-danger hidden'></div>
            <div id='completed_message' class='completed_message alert alert-success hidden'>
              You're in! We'll let you know when it's back. <a href='#' class='klaviyo-bis-close'>Close</a>
            </div>
          </form>
          <p class='small-print'></p>
        </div>
      </div>
    </div>
    </klaviyo-bis></div>`;
    } else {
      html += `<span class="product-cta text-dark uppercase cursor-pointer font-bold" data-handle="${
        item.handle
      }" data-color="${selectedColor}" data-id="${this.decodeId(
        item.variants.edges[0].node.id
      )}">`;
      // if amazon button text is not null, use it, otherwise use default text
      if (item.metafields[2]?.value != null) {
        html += `${item.metafields[2].value}`;
      } else {
        html += `Add to Cart`;
      }
      html += `
            </span>
          </div>
            `;
    }

    return html;
  }

  // ============================================================================
  // RENDERING METHODS - HTML generation and DOM updates
  // ============================================================================
  /**
   * Render the product grid with fetched products
   * @param {Array} products - Array of product data
   * @param {boolean} append - Whether to append to existing grid or replace
   */
  renderGrid(products, append) {
    const relatedSwatches = [];
    this.cursor = `${products[products.length - 1]?.cursor}`;
    let productCards = '';

    // Get existing product IDs to avoid duplicates when appending
    const existingIds = append ? this.getExistingProductIds() : new Set();

    products.forEach((product) => {
      // Skip products that already exist in the DOM
      const productId = String(this.decodeId(product.node.id));
      if (existingIds.has(productId)) {
        return;
      }
      productCards += this.renderTemplate(product);

      if (product.node.metafields[4]) {
        const arr = JSON.parse(product.node.metafields[4].value);
        arr.forEach((id) => {
          relatedSwatches.push(id);
        });
      }
    });

    if (relatedSwatches.length) {
      this.getRelatedProduct(relatedSwatches)
        .then((html) => {
          productCards += html;
          this.updateProductGridHTML(productCards, append);
          this.processSwatches();
        })
        .catch((error) =>
          console.error('Error fetching related products:', error)
        );
    } else {
      this.updateProductGridHTML(productCards, append);
      this.processSwatches();
    }

    this.updateProductVisibility();
  }

  /**
   * Update the product grid HTML content
   * @param {string} productCards - HTML string of product cards
   * @param {boolean} append - Whether to append or replace content
   */
  updateProductGridHTML(productCards, append) {
    const grid = this.querySelector(Merch.SELECTORS.PRODUCTS_GRID);
    if (grid) {
      if (append) {
        grid.innerHTML += productCards;
      } else {
        grid.innerHTML = productCards;
      }
    }
  }

  /**
   * Process swatches for color classes and cleanup
   */
  processSwatches() {
    const swatches = document.querySelectorAll('.swatch');

    swatches.forEach((swatch) => {
      const swatchId = swatch.dataset.swatch;

      // Skip if no ID is present - this is expected for single-color swatches
      if (!swatchId) {
        return;
      }

      // Find any element with this ID as a class (matching jQuery behavior: $(`.${handle}`))
      // Escape the selector since CSS class names starting with numbers need to be escaped
      const escapedSwatchId = CSS.escape(swatchId);
      const productElement = document.querySelector(`.${escapedSwatchId}`);

      if (productElement) {
        const color = productElement.dataset.color;

        if (color) {
          const colorClass = color
            .toLowerCase()
            .replaceAll(' ', '-')
            .replaceAll('/', '-');
          swatch.classList.add(colorClass);
          labelSwatch(swatch, color);
        }
      }
    });
  }

  /**
   * Update product visibility based on page and screen size
   */
  updateProductVisibility() {
    const isDesktop = window.innerWidth > 1024;
    const productNum = isDesktop ? this.page * 32 : this.page * 16;
    const productItems = document.querySelectorAll('.product-grid-item');

    productItems.forEach((item, index) => {
      if (index < productNum) {
        item.classList.remove('!hidden');
      }
    });
  }

  /**
   * Fetch related products by their IDs
   * @param {Array} ids - Array of product IDs to fetch
   * @returns {Promise<string>} Promise that resolves to HTML string of related products
   */
  async getRelatedProduct(ids) {
    const query = `query @inContext(country: ${this.context}) {
          nodes(ids: ${JSON.stringify(ids)}) {
            ... on Product {
              id
              handle
              title
              featuredImage {
                  transformedSrc(maxWidth: 400)
              }
          buttonDisabled: metafield(namespace: "custom", key: "product_button_disabled") { value }

          popupEnabled: metafield(namespace: "custom", key: "klaviyo_popup_enabled") { value }
              popupFormId: metafield(namespace: "custom", key: "klaviyo_form_id") { value }
              popupButtonText: metafield(namespace: "custom", key: "klaviyo_button_text") { value }
              metafields(identifiers: [
            {namespace: "custom", key: "swatch"}, 
            {namespace: "pdp", key: "amazon_link"}, 
            {namespace: "pdp", key: "amazon_button_text"}, 
            {namespace: "pdp", key: "announcement_tag"}, 
            {namespace: "custom", key: "related_swatches"}
          ]) {
                  value
              }
              tags
              variants(first: 100) {
                  edges {
                      node {
                          id
                          price {
                              amount
                          }
                          title
                          availableForSale
                          quantityAvailable
                      }
                  }
              }
              options {
                  name
                  values
              }
            }
          }  
        }`;

    try {
      const response = await fetch(this.graphUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this.accessToken,
          'Content-Type': 'application/graphql',
        },
        body: query,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL errors in getRelatedProduct:', data.errors);
        return '';
      }

      const products = data.data?.nodes || [];
      let html = '';

      products.forEach((product) => {
        if (product) {
          html += this.renderTemplate(product, 'parent');
        }
      });

      return html;
    } catch (error) {
      console.error('Error fetching related products:', error);
      return '';
    }
  }

  // ============================================================================
  // DATA FETCHING METHODS - GraphQL API calls and data processing
  // ============================================================================
  /**
   * Fetch products from Shopify GraphQL API
   * @param {boolean} append - Whether to append to existing grid or replace
   */
  async fetchProducts(append = false) {
    try {
      const response = await fetch(this.graphUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this.accessToken,
          'Content-Type': 'application/graphql',
        },
        body: this.buildProductQuery(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        return;
      }

      const products = data.data?.collection?.products;
      if (!products) {
        console.error('No products data received');
        return;
      }

      if (products.edges.length === 0) {
        console.log('No products found for this collection');
      } else {
        this.renderGrid(products.edges, append);
      }

      this.hasNextPage = products.pageInfo.hasNextPage;
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }
}
customElements.define('merch-homepage', Merch);
